import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import * as desksSelectors from 'store/desksSlice/desksSelectors';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';
import AiqDialog from 'components/UI/Dialogs/AiqDialog';
import CameraStreamFooter from './cameraStreamFooter/CameraStreamFooter';

import CameraStreamHeader from './cameraStreamHeader/CameraStreamHeader';
import Chip from '@mui/material/Chip';
import { logInfo } from 'utils/utils';
import StreamStateNotification from './streamStateNotifications/StreamStateNotification';
import RampActions from '../../transactionSideBar/rampActions/RampActions';

import DigitalZoomWraper from '../utils/DigitalZoom';
import { Rotate } from '../utils/Rotate';
import useRealTimeCameraStreamBadge from 'hooks/transaction/videoWall/useRealTimeCameraStreamBadge';

import usePTZRegion from './PTZ/usePTZRegion';
import ClickOutside from './cameraStreamFooter/clickOutside/clickOutside';
import { useCameraFilters } from './cameraStreamFooter/FooterButtons/BrightnessContrastAdjustment/useCameraFilters';
import styles from './CameraStream.module.css';
import TransactionAssignmentActions from '../../VideoWallMode/CompactMode/CameraStreamCompactHeader/TransactionAssignmentActions';
import { useCameraStreamState } from 'components/transaction/components/panels/transaction/cameras/cameraStream/hooks/useCameraStream';
import { isCameraStreamLogsEnabled } from 'services/CameraStream/config';
import { buildStreamTelemetryContext, webRtcTelemetry } from 'services/StreamingObservability/WebRtcTelemetryService';
import useTransactionBaseStreamType from 'hooks/transaction/useTransactionBaseStreamType';
import useTransactionStreamTypeSync from 'hooks/transaction/useTransactionStreamTypeSync';
import { resolveShownStreamType } from 'utils/transactionSubStream';

const CameraStream = ({
    camConfig,
    transactionInfo,
    tabId,
    expandedCamId,
    expandCamera,
    tabAudioConfig,
    level1,
    L2ContainerId,
    expandedL2ContainerId,
    handleRestartVideoStream,
    handleVideoStarted,
    toggleAudio,
    enableDemoAudioFile,
    isFullScreenRequested,
    fullScreenElementId,
    footerMode,
    l1CamerasId,
    isTabActive,
    videoWallCameras,
    videoWallTransactionNotification,
    streamingCameraIds,
    previousStream,
    nextStream,
    isStreamTestPage = false,
    openWindowVideoIdOutput = '',
    forcedVideoStyles = {},
    forcedStreamType = '',
    visibleCameraIds,
    persistedIsPTZOn,
    onPTZOnChange,
    syncPTZWithExpand = true,
}) => {
    const divRef = useRef();
    const digitalZoomRef = useRef();
    const rotation = useRef(null);
    const freezedTimersRef = useRef({});
    const [openDialog, setOpenDialog] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const currentDesk = useSelector(desksSelectors.selectCurrentDesk);
    const isCompactVideoWallMode = useSelector(transactionsSelectors.selectCompactVideoWallMode);
    const isCompactVideoWall = isCompactVideoWallMode && tabId === 'wall';

    const { id: deskId, name: deskName } = currentDesk || {};
    const { id: cameraPosition, camId: cameraId, gateCameraId, motionDetectionCamera, generatedName, kurentoId } = camConfig;
    // SUB instead of the config's MAIN when the transactionSubStreamConfigEnabled flag matches this desk and camera
    const baseStreamType = useTransactionBaseStreamType({ camConfig, tabId, forcedStreamType });
    const { id: transactionId } = transactionInfo || {};
    const elementId = `${transactionId ? `${transactionId}-` : ''}${cameraPosition}`;
    const isExpanded = expandedCamId === camConfig.id;
    const isFullScreen = fullScreenElementId === elementId;
    const streamTypeShow = resolveShownStreamType({ baseStreamType, isExpanded, isFullScreen, forcedStreamType, tabId });
    const videoOutputId = openWindowVideoIdOutput || cameraStreamManager.getVideoElementId(camConfig?.transactionGroupId || tabId, cameraPosition);
    // tabAudioConfig is nullish for freshly-mounted wall tiles rendered before the tab's audio config
    // has resolved; guard so destructuring never throws during that first render.
    const { availableAudioStreams } = tabAudioConfig || {};
    const assignee = videoWallTransactionNotification?.transactions?.[0]?.assignee ?? '';

    const telemetryContext = useMemo(
        () =>
            buildStreamTelemetryContext({
                camConfig,
                transactionInfo,
                currentDesk,
                videoOutputId,
                streamType: streamTypeShow,
                tabId,
            }),
        [camConfig, currentDesk, streamTypeShow, tabId, transactionInfo, videoOutputId]
    );

    const { getSettings } = useCameraFilters(videoWallCameras);

    const {
        togglePTZActive,
        togglePTZCameraEnlarged,
        isPTZOn,
        isPTZCamera,
        canvasRef,
        canvasInlineStyles,
        initializeNewPTZRegion,
        ptzRequestStatus,
        fullZoomIn,
        fullZoomOut,
    } = usePTZRegion(camConfig, isFocused, persistedIsPTZOn, onPTZOnChange);
    const isNewCamera = useRealTimeCameraStreamBadge(gateCameraId);

    const logCameraFreeze = useCallback(
        (stateUpdate) => {
            const { isLoading, error, freezed } = stateUpdate;
            const isFreezeActive = !isLoading && !error && freezed;

            if (!freezedTimersRef.current[cameraId]) {
                freezedTimersRef.current[cameraId] = { start: null, duration: null };
            }
            const freezeObj = freezedTimersRef.current[cameraId];

            if (isFreezeActive) {
                if (!freezeObj.start) {
                    freezeObj.start = Date.now();
                    freezeObj.duration = null;
                    webRtcTelemetry.record({
                        ...telemetryContext,
                        eventType: 'video_frozen',
                        frozen: true,
                        failureReason: 'frozen_video_detected',
                    });
                }
            } else {
                if (freezeObj.start) {
                    freezeObj.duration = Date.now() - freezeObj.start;
                    webRtcTelemetry.record({
                        ...telemetryContext,
                        eventType: 'video_freeze_recovered',
                        frozen: false,
                        frozenDurationMs: freezeObj.duration,
                        outcome: 'frames_advancing',
                    });
                    console.log(
                        `[CAMERA FREEZED] deskId=${deskId} deskName=${deskName} kurentoId=${kurentoId} cameraId=${cameraId} cameraPath=${generatedName}: camera was frozen for ${freezeObj.duration}ms`
                    );
                    delete freezedTimersRef.current[cameraId];
                }
            }
        },
        [cameraId, deskId, deskName, generatedName, kurentoId, telemetryContext]
    );

    const { streamState, isStreamActivated, videoRef } = useCameraStreamState({
        cameraId,
        streamType: baseStreamType,
        playStreamType: streamTypeShow,
        isTabActive,
        videoOutputId,
        transactionGroupId: camConfig?.transactionGroupId,
        tabId,
        transactionId,
        onStreamStateUpdate: logCameraFreeze,
    });

    useTransactionStreamTypeSync({ streamTypeShow, isExpanded, tabId, isTabActive, isStreamActivated, videoOutputId });

    const enableAudio = streamState.audioEnabled && isTabActive;

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const recordVideoEvent = (eventType, detail = {}) => {
            webRtcTelemetry.recordVideoEvent(
                {
                    ...telemetryContext,
                    eventType,
                    ...detail,
                },
                video
            );
        };

        const handleWaiting = () => recordVideoEvent('video_waiting', { stalled: true, failureReason: 'video_waiting' });
        const handleStalled = () => recordVideoEvent('video_stalled', { stalled: true, failureReason: 'video_stalled' });
        const handleLoadedData = () => recordVideoEvent('video_loaded_data', { outcome: 'loaded_data' });
        const handleEmptied = () => recordVideoEvent('video_emptied', { playing: false, failureReason: 'video_emptied' });
        const handleError = () =>
            recordVideoEvent('video_error', {
                playing: false,
                failureReason: video.error?.code ? `media_error_${video.error.code}` : 'video_error',
            });

        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('stalled', handleStalled);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('emptied', handleEmptied);
        video.addEventListener('error', handleError);

        return () => {
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('stalled', handleStalled);
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('emptied', handleEmptied);
            video.removeEventListener('error', handleError);
        };
    }, [telemetryContext, videoRef]);

    useEffect(() => () => rotation.current?.destroy(), []);

    useEffect(() => {
        if (!syncPTZWithExpand) return;
        isPTZCamera && togglePTZCameraEnlarged(isExpanded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, syncPTZWithExpand]);

    useEffect(() => {
        if (isExpanded) {
            console.log(`[CAMERA ENLARGED] cameraId=${cameraId} cameraPath=${generatedName}: camera stream enlarged`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

    const expandCameraHandler = function () {
        expandCamera && expandCamera(camConfig.id, L2ContainerId, false, baseStreamType);
    };

    const toggleAudioHandler = useCallback(
        function (e) {
            if (!availableAudioStreams?.includes(videoOutputId)) return;
            cameraStreamManager.enableAudio(videoOutputId, e?.muteAll);
            toggleAudio && toggleAudio(videoOutputId, tabId, e?.muteAll);
        },
        [availableAudioStreams, videoOutputId, tabId, toggleAudio]
    );

    const videoStartedHandler = function (e) {
        if (isCameraStreamLogsEnabled()) console.log(`Video element ${videoOutputId} onPlaying cameraId=${cameraId}`);
        webRtcTelemetry.recordVideoEvent({ ...telemetryContext, eventType: 'video_playing', outcome: 'playing' }, e.target);
        initializeNewPTZRegion(e.target, divRef, digitalZoomRef);
        if (!rotation.current) rotation.current = new Rotate({ elementId: videoOutputId, zoomWraper: digitalZoomRef.current });
        if (streamState.updateSourceState) streamState.updateSourceState({ info: '', error: '', isLoading: false });
        handleVideoStarted && handleVideoStarted(cameraPosition, tabId);
    };

    const refreshVideoStreamHandler = function () {
        logInfo(`[STREAM REFRESH] refreshType="STANDARD"`, { transactionInfo, camConfig, camVideoId: videoOutputId });
        webRtcTelemetry.recordVideoEvent({ ...telemetryContext, eventType: 'soft_refresh_attempted', outcome: 'requested' }, videoRef.current);
        cameraStreamManager.restart(videoOutputId, streamTypeShow);
        handleRestartVideoStream && handleRestartVideoStream(cameraPosition, tabId);
    };

    const hardRefreshVideoStreamHandler = function () {
        logInfo(`[STREAM REFRESH] refreshType="HARD"`, { transactionInfo, camConfig, camVideoId: videoOutputId });
        webRtcTelemetry.recordVideoEvent(
            {
                ...telemetryContext,
                eventType: 'hard_refresh_attempted',
                hardRefreshAttempted: true,
                hardRefreshOutcome: 'requested',
            },
            videoRef.current
        );
        cameraStreamManager.hardRefresh(videoOutputId);
    };

    const openDialogHandler = function () {
        setOpenDialog(true);
    };
    const closeDialogHandler = function () {
        setOpenDialog(false);
    };
    const hasRelays = camConfig.relays;
    const relays =
        hasRelays &&
        camConfig.relays.map((relayConfig) => (
            <RampActions key={`relay-${relayConfig.relaySlotId}-${relayConfig.name}`} config={relayConfig} classes={styles.relays} />
        ));

    const cameraStreamContainerStyles = `${styles['tc-cam-views']} ${isCompactVideoWall ? ` ${styles['tc-cam-views--compact']}` : ''} ${isNewCamera ? ` ${styles['tc-cam-views--updated']}` : ''} ${isNewCamera && isCompactVideoWall ? ` ${styles['tc-cam-views-compact-mode--updated']}` : ''} ${motionDetectionCamera ? ` ${styles['tc-cam-views--motion']}` : ''}${
        isFocused ? ` ${styles['tc-cam-views--focused']}` : ''
    }${expandedCamId && isExpanded ? ` ${styles['tc-cam-views--expanded']}` : ''}${
        expandedCamId && !level1 && expandedL2ContainerId === L2ContainerId && expandedCamId !== camConfig.id
            ? ` ${styles['tc-cam-views--hidden']}`
            : ''
    }${isFullScreen ? ` ${styles['tc-cam-views--fullscreen']}` : ''}`;

    const videoContainerStyles = `${styles['video-container']}${isExpanded && hasRelays ? ` ${styles['video-container--left']}` : ''}`;
    const videoStyles = getVideoStyles(expandedCamId, camConfig, level1, videoWallCameras, isCompactVideoWall);

    const videoContainerId = `video-container__${videoOutputId}`;

    const showTransactionAssignmentActions = l1CamerasId && !isCompactVideoWall && videoWallCameras && !assignee;

    const brightnessContrasData = getSettings(cameraId);
    const brightnessFilter = 1 + brightnessContrasData?.brightness / 100;
    const contrastFilter = 1 + brightnessContrasData?.contrast / 100;

    return (
        <ClickOutside
            clickOutsideFunction={() => setIsFocused(false)}
            isOn={isFocused}
            className={cameraStreamContainerStyles}
            id={elementId}
            onMouseDownCapture={() => setIsFocused(true)}
            onTouchStartCapture={() => setIsFocused(true)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <CameraStreamHeader
                hovered={hovered || footerMode}
                isVideoLoading={streamState.isLoading}
                videoFailedToLoad={!!streamState.error}
                camConfig={camConfig}
                level1={level1}
                tabAudioConfig={tabAudioConfig}
                tabId={tabId}
                expandedCamId={expandedCamId}
                isFullScreenRequested={isFullScreenRequested}
                headerMode={l1CamerasId}
                videoWallTransactionNotification={videoWallTransactionNotification}
                isCompactVideoWall={isCompactVideoWall}
                isDetectionOn={camConfig?.isAIDetectionOn}
                isIntercomActive={camConfig?.activeWebhook}
                hasIntercom={camConfig?.hasIntercom}
            ></CameraStreamHeader>
            {showTransactionAssignmentActions && (
                <TransactionAssignmentActions
                    cameraId={gateCameraId}
                    hasTransaction={!!videoWallTransactionNotification}
                    assignee={assignee}
                    className={styles[`transaction-assignment-actions${hovered ? '--hovered' : ''}`]}
                    assignOnClick={!!videoWallTransactionNotification}
                />
            )}
            <div
                className={videoContainerStyles}
                onDoubleClick={() => {
                    if (isCompactVideoWall) return;
                    expandCameraHandler();
                }}
            >
                <div className={videoStyles} id={videoContainerId} ref={divRef}>
                    <DigitalZoomWraper
                        config={{
                            disabled: videoWallCameras && !isExpanded,
                            limitToBounds: true,
                            ref: digitalZoomRef,
                        }}
                        reset={!isExpanded}
                    >
                        <video
                            id={videoOutputId}
                            onPlaying={videoStartedHandler}
                            muted={!enableAudio}
                            style={{ ...forcedVideoStyles, filter: `brightness(${brightnessFilter}) contrast(${contrastFilter})` }}
                            ref={videoRef}
                        />
                        <StreamStateNotification isCameraActivatedInCC={camConfig.status === 'ACTIVE'} streamState={streamState} />
                    </DigitalZoomWraper>
                    {isPTZCamera && <canvas ref={canvasRef} style={canvasInlineStyles} />}
                </div>
            </div>
            <CameraStreamFooter
                hovered={hovered}
                isVideoLoading={streamState.isLoading}
                videoFailedToLoad={!!streamState.error}
                camConfig={camConfig}
                refreshVideoStreamHandler={refreshVideoStreamHandler}
                hardRefreshVideoStreamHandler={hardRefreshVideoStreamHandler}
                level1={level1}
                openDialogHandler={openDialogHandler}
                tabAudioConfig={tabAudioConfig}
                tabId={tabId}
                expandedCamId={expandedCamId}
                onToggleAudio={toggleAudioHandler}
                enableDemoAudioFile={enableDemoAudioFile}
                isFullScreenRequested={isFullScreenRequested}
                footerMode={footerMode}
                cameraIssues={camConfig.issues}
                transactionId={transactionId}
                camInfo={{
                    transactionInfo,
                    camVideoId: videoOutputId,
                    camConfig,
                }}
                videoWallCameras={videoWallCameras}
                rotation={rotation.current}
                togglePTZ={togglePTZActive}
                isPTZOn={isPTZOn}
                isCameraFocused={isFocused}
                ptzRequestStatus={ptzRequestStatus}
                isPTZCamera={isPTZCamera}
                fullZoomIn={fullZoomIn}
                fullZoomOut={fullZoomOut}
                isTabActive={isTabActive}
                isDisabledSnapshot={(streamState.info && !streamState.error) || !!streamState.error}
                streamingCameraIds={streamingCameraIds}
                previousStream={previousStream}
                nextStream={nextStream}
                isCameraFreezed={!streamState.isLoading && !streamState.error && streamState.freezed}
                isStreamTestPage={isStreamTestPage}
                visibleCameraIds={visibleCameraIds}
                openWindowVideoIdOutput={openWindowVideoIdOutput}
            />
            {isStreamActivated && openDialog && (
                <AiqDialog
                    title={`${videoOutputId} debug info`}
                    text={cameraStreamManager.getInfo(videoOutputId)}
                    open={openDialog}
                    onClose={closeDialogHandler}
                ></AiqDialog>
            )}
            {isExpanded && hasRelays && (
                <div className={styles['relays-container']}>
                    <div className={styles['relays-title']}>Relay triggers</div>
                    <div className={styles['relays-disclaimer']}>Visually confirm that the gate is fully closed</div>
                    {relays}
                </div>
            )}
            {isNewCamera && !isCompactVideoWall && (
                <Chip
                    label="New"
                    sx={{
                        background: 'var(--info-main)',
                        position: 'absolute',
                        left: '0.938rem',
                        bottom: '0.313rem',
                        fontSize: '12px',
                        color: '#fff',
                        lineHeight: '20px',
                        letterSpacing: '0.14px',
                        padding: '0 0.406rem',
                    }}
                />
            )}
        </ClickOutside>
    );
};

export default memo(CameraStream);

function getVideoStyles(expandedCamId, camConfig, level1, videoWallCameras, isCompactVideoWall) {
    return `${isCompactVideoWall ? ` ${styles['tc-cs-video-compact-mode']}` : `${styles['tc-cs-video']}`}${
        expandedCamId
            ? expandedCamId === camConfig.id
                ? ` ${styles['tc-cs-video-expanded']}${videoWallCameras ? ` ${styles['tc-cs-video-expanded__video-wall']}` : ''}`
                : ` ${styles['tc-cs-video-shrinked']}`
            : ''
    }${!level1 ? ` ${styles['tc-cs-video--level2']}` : ''} ${videoWallCameras ? `${styles['tc-cs-video-videoWall']}` : ''} ${isCompactVideoWall ? ` ${styles['tc-cs-video-compact-mode']}` : ''}`;
}
